import {Checkout, Customer, IGetCheckoutShippingOptionsData} from "@connox/typescript-library";
import * as Sentry from "@sentry/browser";
import * as React from "react";
import {useRef, useState} from "react";
import {IViewState, useCartContext} from "../../cart/CartContextProvider/CartContextProvider";
import {useCustomerContext} from "../../customer/CustomerContextProvider/CustomerContextProvider";
import {PaymentController, Payments} from "../../lib/payments/PaymentController";
import window from "../../lib/WindowExtended";
import {CheckoutStep} from "../Checkout";
import {useCheckoutContext} from "./CheckoutContextProvider";
import {isAmazonLoggedIn} from "../../lib/payments/AmazonPayLibrary";
import {isToNorthIreland} from "../steps/AddressStep/AddressStep";
import {sentryAddBreadcrumb} from "../../../helpers/sentryHelper";

interface IAmazonState {
  isAddressNeeded: boolean;
  isPaymentNeeded: boolean;
}

interface ICheckoutViewContext {
  deliveryCountry?: string;
  step: CheckoutStep;
  gotoStep: (step?: CheckoutStep, viewState?: IViewState) => void;
  shippingOptions: IGetCheckoutShippingOptionsData[];
}

const checkoutViewContext = React.createContext<ICheckoutViewContext | null>(null);

export const useCheckoutViewContext = (): ICheckoutViewContext => {
  const context = React.useContext(checkoutViewContext);
  if (!context) {
    throw new Error("useCheckoutViewContext must be used in a component within a CheckoutViewContextProvider.");
  }

  return context;
};

export const isAmazonCheckoutAuthorized = (): boolean => {
  const uParams = new URLSearchParams(window.location.search);

  const gotoStep = uParams.get("gotoStep") === CheckoutStep.SUMMARY;
  const AuthStatus = uParams.get("AuthenticationStatus") === "Success";

  return gotoStep && AuthStatus;
};

export const amazonState = (paymentId: number | null): IAmazonState => {
  const tempState = {
    isAddressNeeded: false,
    isPaymentNeeded: false,
  };

  if (paymentId === Payments.AmazonPay && window.orderReferenceId) {
    tempState.isAddressNeeded = !window.isBillingAgreementAddressSet;
    tempState.isPaymentNeeded = !window.isBillingAgreementPaymentSet;
  }

  return tempState;
};

export const isExternalPayment = (): boolean => !!(window.pp_nonce || window.orderReferenceId);

const isValidForSkippingOverAddressStep = (checkout: Checkout): boolean => {
  if (isToNorthIreland(checkout.deliveryAddress?.countryCode ?? "", checkout.deliveryAddress?.zip ?? "")) {
    return false;
  }
  // if addresses valid, return true to skip address step
  if (isAddressesValid(checkout)) {
    return true;
  }

  // if amazon pay user, return true (will be skipped by amazonState(paymentId).isAddressNeeded)
  // if not apay user, return false. seems to be errors in addresses

  return window.isIntoSettingPhoneNoInShippingStep || isAmazonLoggedIn();
};

const toStopAtShippingStep = (checkout: Checkout, shippingOptions: IGetCheckoutShippingOptionsData[] | undefined) => {
  return (
    !checkout.logisticsTypeId ||
    !shippingOptions?.find((ship) => ship.logisticsTypeId === checkout.logisticsTypeId) ||
    checkout.cartData.messages.find((msg) => msg.code === "NO_SHIPPING_SELECTED") ||
    (shippingOptions?.find((ship) => ship.logisticsTypeId === checkout.logisticsTypeId)?.isPhoneRequired &&
      !isAmazonLoggedIn() &&
      (!checkout.deliveryAddress?.phone || !!checkout.deliveryAddress?.messages?.find((msg) => msg.field === "phone")))
  );
};

/**
 * return appropriate step if none is given
 * @param checkout
 * @param customer
 * @param viewState
 * @param shippingOptions
 */
const returnAppropriateStep = (
  checkout: Checkout,
  customer: Customer,
  viewState: IViewState,
  shippingOptions?: IGetCheckoutShippingOptionsData[],
): CheckoutStep => {
  const isItemPositionsInCart = !!checkout.cartData.positions.find(
    (position) => position.reference && position.reference.itemId !== null && !!position.item,
  );

  let returnStep: CheckoutStep;
  if ((isAmazonCheckoutAuthorized() && isItemPositionsInCart) || window.amazonPayAuthorized) {
    return checkout.isCompleted ? CheckoutStep.FINISHED : CheckoutStep.SUMMARY;
  }

  if (!isItemPositionsInCart) {
    returnStep = CheckoutStep.CARTERRORS;
  } else if (!(customer.isLoggedIn || viewState.isRegistering || isExternalPayment())) {
    returnStep = CheckoutStep.LOGIN;
  } else if (
    !isValidForSkippingOverAddressStep(checkout) ||
    amazonState(checkout.paymentId).isAddressNeeded ||
    !!checkout.cartData.positions.find((pos) => {
      return !!pos.messages.find((msg) => msg.code === "NOT_ALLOWED_FOR_COUNTRY" || msg.code === "SHIPPING_NOT_FOUND");
    }) ||
    window.isIntoChangingCountry
  ) {
    returnStep = CheckoutStep.ADDRESS;
  } else if (toStopAtShippingStep(checkout, shippingOptions)) {
    returnStep = CheckoutStep.SHIPPING;
  } else if (
    !checkout.paymentId ||
    amazonState(checkout.paymentId).isPaymentNeeded ||
    !PaymentController.isDataComplete()
  ) {
    returnStep = CheckoutStep.PAYMENT;
  } else if (!checkout.isCompleted) {
    returnStep = CheckoutStep.SUMMARY;
  } else {
    returnStep = CheckoutStep.FINISHED;
  }

  return returnStep;
};

/**
 * Context provider for view params
 * @param props
 * @constructor
 */
const CheckoutViewContextProvider: React.FC<{children?: React.ReactNode}> = (props) => {
  const {customer} = useCustomerContext();
  const {setViewState, viewState} = useCartContext();
  const {checkout} = useCheckoutContext();
  const viewStateRef = useRef<IViewState>(viewState);
  const [checkoutViewContextState, setCheckoutViewContextState] = useState<ICheckoutViewContext | null>(null);
  const checkoutViewRef = useRef<ICheckoutViewContext | null>();

  checkoutViewRef.current = checkoutViewContextState;
  viewStateRef.current = viewState;

  const gotoStep = (nextStep?: CheckoutStep, stateOfView?: IViewState) => {
    if (stateOfView !== undefined) {
      viewStateRef.current = stateOfView;
      setViewState(stateOfView);
    }
    const tempNextStep =
      nextStep ||
      returnAppropriateStep(checkout, customer, stateOfView || viewState, checkoutViewRef.current?.shippingOptions);
    const reason = nextStep ? "Breadcrumb or Summary" : "continue from amazon pay wallet widget";
    sentryAddBreadcrumb(tempNextStep, checkout, reason, checkoutViewRef.current?.shippingOptions, customer);
    setCheckoutViewContextState((prevState) => {
      if (!prevState) {
        return prevState;
      }

      return {
        ...prevState,
        step: tempNextStep,
      };
    });
  };

  const checkoutEmitSetAllThings = async () => await setAllThings("checkout emit");
  const customerEmitSetAllThings = async () => await setAllThings("customer emit");

  const setAllThings = async (trigger: string) => {
    const cartCountry = checkout.cartData.countryCode;
    let shippingOptions: IGetCheckoutShippingOptionsData[] = checkoutViewRef.current?.shippingOptions ?? [];

    if (cartCountry !== checkoutViewRef.current?.deliveryCountry || !checkoutViewRef.current?.shippingOptions?.length) {
      try {
        shippingOptions = await checkout.getAvailableShippingOptions();
      } catch (err) {
        shippingOptions = [];
        Sentry.captureException(err, {
          extra: {occurence: "CheckoutViewContext.setAllThings()", countryCode: checkout.cartData.countryCode},
        });
      }
    }

    const appropiateStep = returnAppropriateStep(checkout, customer, viewStateRef.current, shippingOptions);

    sentryAddBreadcrumb(appropiateStep, checkout, trigger, shippingOptions, customer);
    setCheckoutViewContextState(() => ({
      deliveryCountry: cartCountry,
      gotoStep,
      shippingOptions,
      step: appropiateStep,
    }));
  };

  React.useEffect(() => {
    void setAllThings("checkout view init");
    checkout.emitter.on(checkoutEmitSetAllThings);
    customer.emitter.on(customerEmitSetAllThings);

    return () => {
      checkout.emitter.off(checkoutEmitSetAllThings);
      customer.emitter.off(customerEmitSetAllThings);
    };
  }, []);

  if (!checkoutViewContextState) {
    return null;
  }

  const {Provider} = checkoutViewContext;

  return (
    <Provider
      value={{
        step: checkoutViewRef.current?.step ?? CheckoutStep.CARTERRORS,
        gotoStep,
        shippingOptions: checkoutViewRef.current?.shippingOptions ?? [],
      }}
    >
      {props.children}
    </Provider>
  );
};

/**
 * Check addresses for errors
 * @param checkout
 */
const isAddressesValid = (checkout: Checkout): boolean => {
  const {deliveryAddress, invoiceAddress} = checkout;

  return !(
    deliveryAddress === null ||
    invoiceAddress === null ||
    deliveryAddress.messages?.find((message) => message.type === "error") ||
    invoiceAddress.messages?.find((message) => message.type === "error")
  );
};

export default CheckoutViewContextProvider;
